import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import {
  Search,
  X,
  Plus,
  AlertTriangle,
  Lock,
  ChevronRight,
  ChevronDown,
} from 'lucide-react';
import { useApp, FilterStatus, SortDir } from '@/store';
import { useT } from '@/lib/i18n';
import { fmtDate } from '@/lib/utils';
import { deviceTypeLabel, resolveDeviceCounts } from '@/lib/device-type-label';
import { HOSE_COUNT_KEY } from '@/lib/device-counts';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { StatusDot } from '@/components/StatusBadge';
import { Status, type Customer, type DeviceType } from '@/data/types';
import { apiFetch } from '@/lib/api';
import { getDecoded } from '@/lib/auth';
import { deriveNextInspection } from '@/lib/customer-dates';
import { ScanButton } from '@/components/BarcodeScanner';
import { useIncrementalRender } from '@/lib/useIncrementalRender';
import { SectionId } from '@/lib/section-permissions';
import { useSectionRights } from '@/lib/use-section-rights';

type DupMatch = { id: string; name: string; city?: string };

// The per-type columns used to be a hardcoded slug list that matched no id any
// producer ever writes, so five of six columns were always empty and the
// headers showed raw slugs. Derive them from the type keys actually present in
// the data instead, labelled through the device-type catalogue (client #80).
const HOSE_COL = HOSE_COUNT_KEY;

const usedDeviceCols = (
  customers: Customer[],
  deviceTypes: DeviceType[]
): string[] => {
  const seen = new Set<string>();
  customers.forEach(c =>
    Object.entries(resolveDeviceCounts(c.deviceCounts, deviceTypes)).forEach(
      ([k, v]) => {
        if (v > 0) seen.add(k);
      }
    )
  );
  // Hoses last so the extinguisher types read together.
  const keys = [...seen].filter(k => k !== HOSE_COL).sort();
  return seen.has(HOSE_COL) ? [...keys, HOSE_COL] : keys;
};

const customerTotal = (c: Customer) =>
  Object.values(c.deviceCounts ?? {}).reduce((s, v) => s + v, 0);

const getSortValue = (c: Customer, col: string): string | number => {
  if (col === 'city') return c.objectAddress?.city ?? '';
  if (col === 'street') return c.objectAddress?.street ?? '';
  if (col === 'worker') return c.worker ?? '';
  if (col === 'total') return customerTotal(c);
  if (col === 'nextInspection') return deriveNextInspection(c) ?? '';
  return (c as any)[col] ?? '';
};

export function Overview() {
  const t = useT();
  const nav = useNavigate();
  const {
    customers,
    search,
    filterStatus,
    filterWorker,
    sortCol,
    sortDir,
    selectedCustomerIds,
    toggleSelected,
    selectAll,
    setFilter,
    setSort,
    clearFilters,
    workers,
    addCustomer,
    toast,
    deviceTypes,
    lang,
  } = useApp();

  const customerRights = useSectionRights()[SectionId.CUSTOMERS];

  const [newOpen, setNewOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newWorker, setNewWorker] = useState('');
  const [dupMatches, setDupMatches] = useState<DupMatch[] | null>(null);
  const [viewMode, setViewMode] = useState<'list' | 'billing'>('list');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const toggleGroup = (key: string) =>
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const createCustomer = () => {
    const name = newName.trim();
    const id = `C-${Date.now()}`;
    const decoded = getDecoded();
    addCustomer({
      id,
      name,
      status: Status.NONE,
      worker: newWorker || undefined,
      created: new Date().toISOString().slice(0, 10),
      createdBy: decoded?.email ?? decoded?.sub ?? undefined,
      billingAddress: { company: name, country: 'CH' },
      objectAddress: { company: name, country: 'CH' },
      extinguishers: [],
      hoses: [],
      archives: [],
    });
    toast(t('overview.newCustomer'));
    setNewOpen(false);
    setNewName('');
    setNewWorker('');
    setDupMatches(null);
    nav(`/customer/${id}`);
  };

  const localMatches = (name: string): DupMatch[] =>
    customers
      .filter(c => c.name.trim().toLowerCase() === name.toLowerCase())
      .map(c => ({ id: c.id, name: c.name, city: c.objectAddress?.city }));

  const handleNewCustomer = async () => {
    const name = newName.trim();
    if (!name) return;
    if (!dupMatches) {
      // Authoritative tenant-wide check via API; fall back to the in-memory
      // store when offline / API unreachable.
      let matches: DupMatch[];
      try {
        const res = await apiFetch<{ matches: DupMatch[] }>(
          `/customers/exists?name=${encodeURIComponent(name)}`
        );
        matches = res.matches;
      } catch {
        matches = localMatches(name);
      }
      if (matches.length) {
        setDupMatches(matches);
        return;
      }
    }
    createCustomer();
  };

  const filtered = useMemo(() => {
    let list = customers.filter(c => {
      const q = search.toLowerCase().trim();
      if (q) {
        const deviceNumbers = [
          ...c.extinguishers.map(e => e.deviceNumber ?? ''),
          ...c.hoses.map(h => h.deviceNumber ?? ''),
        ].join(' ');
        // Address line 2 carries the building / department on a lot of records,
        // so leaving it out of the haystack made those sites unfindable
        // (client #59). Billing street/city/zip are searchable for the same
        // reason — the office searches by whatever is on the paperwork.
        const addr = (a?: typeof c.billingAddress) =>
          a
            ? `${a.company ?? ''} ${a.attn ?? ''} ${a.addition ?? ''} ${a.street ?? ''} ${a.zip ?? ''} ${a.city ?? ''}`
            : '';
        const hay =
          `${c.name} ${c.id} ${addr(c.billingAddress)} ${addr(c.objectAddress)} ${addr(c.ownerAddress)} ${deviceNumbers}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (
        filterStatus !== FilterStatus.ALL &&
        (c.status as string) !== filterStatus
      )
        return false;
      if (filterWorker && c.worker !== filterWorker) return false;
      return true;
    });
    list = [...list].sort((a, b) => {
      const av = getSortValue(a, sortCol);
      const bv = getSortValue(b, sortCol);
      const cmp =
        typeof av === 'number' && typeof bv === 'number'
          ? av - bv
          : String(av).localeCompare(String(bv));
      return sortDir === SortDir.ASC ? cmp : -cmp;
    });
    return list;
  }, [customers, search, filterStatus, filterWorker, sortCol, sortDir]);

  const deviceCols = useMemo(
    () => usedDeviceCols(customers, deviceTypes),
    [customers, deviceTypes]
  );
  // Counts resolved onto canonical ids so the two key shapes share a column.
  const resolvedCounts = useMemo(() => {
    const m: Record<string, Record<string, number>> = {};
    customers.forEach(c => {
      m[c.id] = resolveDeviceCounts(c.deviceCounts, deviceTypes);
    });
    return m;
  }, [customers, deviceTypes]);

  const totals = useMemo(() => {
    const tot: Record<string, number> = {};
    filtered.forEach(c => {
      deviceCols.forEach(d => {
        tot[d] = (tot[d] ?? 0) + (resolvedCounts[c.id]?.[d] ?? 0);
      });
    });
    return tot;
  }, [filtered, deviceCols, resolvedCounts]);

  const grandTotal = useMemo(
    () => filtered.reduce((s, c) => s + customerTotal(c), 0),
    [filtered]
  );

  const billingGroups = useMemo(() => {
    const UNGROUPED = ' ungrouped';
    const map = new Map<
      string,
      { key: string; label: string; customers: Customer[]; total: number }
    >();
    filtered.forEach(c => {
      const company = c.billingAddress?.company?.trim();
      let key: string;
      let label: string;
      if (!company) {
        key = UNGROUPED;
        label = t('overview.ungrouped');
      } else {
        const zip = c.billingAddress?.zip?.trim();
        key = zip
          ? `${company.toLowerCase()}|${zip.toLowerCase()}`
          : company.toLowerCase();
        label = company;
      }
      let g = map.get(key);
      if (!g) {
        g = { key, label, customers: [], total: 0 };
        map.set(key, g);
      }
      g.customers.push(c);
      g.total += customerTotal(c);
    });
    return [...map.values()].sort((a, b) => {
      if (a.key === UNGROUPED) return 1;
      if (b.key === UNGROUPED) return -1;
      const byCount = b.customers.length - a.customers.length;
      return byCount !== 0 ? byCount : a.label.localeCompare(b.label);
    });
  }, [filtered, t]);

  const allSelected =
    filtered.length > 0 && filtered.every(c => selectedCustomerIds.has(c.id));

  const {
    visible: visibleRows,
    sentinelRef,
    hasMore,
  } = useIncrementalRender(filtered);

  // Without Lesen on Kunden (#95) the overview shows no customer data at all —
  // it stays reachable because it is the landing page of last resort.
  if (!customerRights.read) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-bold tracking-tight">
          {t('overview.title')}
        </h1>
        <Card className="p-6">
          <div className="flex items-start gap-3">
            <Lock className="h-5 w-5 shrink-0 text-muted-foreground" />
            <div className="space-y-1">
              <div className="font-semibold">{t('perms.noAccessTitle')}</div>
              <p className="text-sm text-muted-foreground">
                {t('perms.noCustomerAccess')}
              </p>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="space-y-4"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            {t('overview.title')}
          </h1>
          <p className="text-sm text-muted-foreground">
            {filtered.length} of {customers.length} {t('overview.customers')}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <Tabs
            value={viewMode}
            onValueChange={v => setViewMode(v as 'list' | 'billing')}
          >
            <TabsList>
              <TabsTrigger value="list">{t('overview.viewList')}</TabsTrigger>
              <TabsTrigger value="billing">
                {t('overview.viewByBilling')}
              </TabsTrigger>
            </TabsList>
          </Tabs>
          {customerRights.create && (
            <Button
              size="sm"
              onClick={() => setNewOpen(true)}
              data-focus="overview-new-customer"
            >
              <Plus className="h-4 w-4" />
              {t('overview.newCustomer')}
            </Button>
          )}
        </div>
      </div>

      <Card className="p-4">
        <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 md:grid-cols-4">
          <div
            className="sm:col-span-2 flex gap-2"
            data-focus="overview-search"
          >
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t('overview.searchPlaceholder')}
                className="pl-9"
                value={search}
                onChange={e => setFilter('search', e.target.value)}
              />
            </div>
            <ScanButton onScan={v => setFilter('search', v)} />
          </div>
          <Select
            value={filterStatus}
            onValueChange={v => setFilter('filterStatus', v)}
          >
            <SelectTrigger>
              <SelectValue placeholder={t('table.status')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('overview.allStatuses')}</SelectItem>
              <SelectItem value="done">{t('status.done')}</SelectItem>
              <SelectItem value="waiting">{t('status.waiting')}</SelectItem>
              <SelectItem value="todo">{t('status.todo')}</SelectItem>
              <SelectItem value="none">{t('status.none')}</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex gap-2">
            <Select
              value={filterWorker || 'all'}
              onValueChange={v =>
                setFilter('filterWorker', v === 'all' ? '' : v)
              }
            >
              <SelectTrigger>
                <SelectValue placeholder={t('customer.worker')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('overview.allWorkers')}</SelectItem>
                {workers.map(w => (
                  <SelectItem key={w.id} value={w.name}>
                    {w.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="ghost"
              size="icon"
              onClick={clearFilters}
              aria-label="Clear filters"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </Card>

      {viewMode === 'list' ? (
        <>
          <Card data-tour="overview-table">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox
                      checked={allSelected}
                      onCheckedChange={v => selectAll(!!v)}
                    />
                  </TableHead>
                  <TableHead className="w-10">●</TableHead>
                  <TableHead
                    className="cursor-pointer hidden sm:table-cell"
                    onClick={() => setSort('id')}
                  >
                    {t('table.id')}
                  </TableHead>
                  <TableHead
                    className="cursor-pointer"
                    onClick={() => setSort('name')}
                  >
                    {t('overview.company')}
                  </TableHead>
                  <TableHead
                    className="cursor-pointer hidden md:table-cell"
                    onClick={() => setSort('street')}
                  >
                    {t('address.street')}
                  </TableHead>
                  <TableHead
                    className="cursor-pointer hidden sm:table-cell"
                    onClick={() => setSort('city')}
                    data-focus="overview-city-sort"
                  >
                    {t('address.city')}
                  </TableHead>
                  <TableHead
                    className="cursor-pointer hidden lg:table-cell"
                    onClick={() => setSort('worker')}
                  >
                    {t('customer.worker')}
                  </TableHead>
                  <TableHead
                    className="cursor-pointer hidden sm:table-cell"
                    onClick={() => setSort('nextInspection')}
                  >
                    {t('overview.nextInsp')}
                  </TableHead>
                  {deviceCols.map(d => (
                    <TableHead
                      key={d}
                      className="text-center hidden lg:table-cell"
                    >
                      {d === HOSE_COL
                        ? t('customer.hoses')
                        : deviceTypeLabel(d, deviceTypes, lang)}
                    </TableHead>
                  ))}
                  <TableHead
                    className="cursor-pointer text-center hidden lg:table-cell"
                    onClick={() => setSort('total')}
                    data-focus="overview-total"
                  >
                    {t('overview.total')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleRows.map((c: Customer) => (
                  <TableRow
                    key={c.id}
                    data-state={
                      selectedCustomerIds.has(c.id) ? 'selected' : undefined
                    }
                  >
                    <TableCell>
                      <Checkbox
                        checked={selectedCustomerIds.has(c.id)}
                        onCheckedChange={() => toggleSelected(c.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <StatusDot status={c.status} />
                    </TableCell>
                    <TableCell className="font-mono text-xs hidden sm:table-cell">
                      {c.id}
                    </TableCell>
                    <TableCell>
                      <Link
                        to={`/customer/${c.id}`}
                        className="font-medium text-primary hover:underline"
                      >
                        {c.name}
                      </Link>
                    </TableCell>
                    <TableCell className="text-muted-foreground hidden md:table-cell">
                      {c.objectAddress?.street}
                    </TableCell>
                    <TableCell className="text-muted-foreground hidden sm:table-cell">
                      {c.objectAddress?.city}
                    </TableCell>
                    <TableCell className="text-muted-foreground hidden lg:table-cell">
                      {c.worker ?? '—'}
                    </TableCell>
                    <TableCell className="text-muted-foreground hidden sm:table-cell">
                      {fmtDate(deriveNextInspection(c))}
                    </TableCell>
                    {deviceCols.map(d => (
                      <TableCell
                        key={d}
                        className="text-center hidden lg:table-cell"
                      >
                        {resolvedCounts[c.id]?.[d] ?? ''}
                      </TableCell>
                    ))}
                    <TableCell className="text-center font-semibold hidden lg:table-cell">
                      {customerTotal(c) || ''}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell
                    colSpan={8}
                    className="text-right hidden lg:table-cell"
                  >
                    Totals {/* i18n-ignore */}
                  </TableCell>
                  <TableCell colSpan={4} className="text-right lg:hidden">
                    Totals {/* i18n-ignore */}
                  </TableCell>
                  {deviceCols.map(d => (
                    <TableCell
                      key={d}
                      className="text-center hidden lg:table-cell"
                    >
                      {totals[d] || ''}
                    </TableCell>
                  ))}
                  <TableCell className="text-center font-semibold hidden lg:table-cell">
                    {grandTotal || ''}
                  </TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </Card>
          {hasMore && <div ref={sentinelRef} aria-hidden className="h-px" />}
        </>
      ) : (
        <div className="space-y-2">
          {billingGroups.map(g => {
            const open = expandedGroups.has(g.key);
            return (
              <Card key={g.key} className="overflow-hidden">
                <button
                  type="button"
                  onClick={() => toggleGroup(g.key)}
                  className="flex w-full items-center gap-3 p-4 text-left hover:bg-muted/50"
                  aria-expanded={open}
                >
                  {open ? (
                    <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                  )}
                  <span className="font-medium truncate">{g.label}</span>
                  <Badge variant="secondary" className="shrink-0">
                    {g.customers.length} {t('overview.sites')}
                  </Badge>
                  <span className="ml-auto shrink-0 text-sm font-semibold text-muted-foreground">
                    {g.total || ''}
                  </span>
                </button>
                {open && (
                  <div className="border-t">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t('overview.company')}</TableHead>
                          <TableHead className="hidden sm:table-cell">
                            {t('address.city')}
                          </TableHead>
                          <TableHead className="hidden sm:table-cell">
                            {t('overview.nextInsp')}
                          </TableHead>
                          <TableHead className="text-center">
                            {t('overview.total')}
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {g.customers.map(c => (
                          <TableRow key={c.id}>
                            <TableCell>
                              <Link
                                to={`/customer/${c.id}`}
                                className="font-medium text-primary hover:underline"
                              >
                                {c.name}
                              </Link>
                            </TableCell>
                            <TableCell className="text-muted-foreground hidden sm:table-cell">
                              {c.objectAddress?.city}
                            </TableCell>
                            <TableCell className="text-muted-foreground hidden sm:table-cell">
                              {fmtDate(deriveNextInspection(c))}
                            </TableCell>
                            <TableCell className="text-center font-semibold">
                              {customerTotal(c) || ''}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
      <Dialog
        open={newOpen}
        onOpenChange={v => {
          setNewOpen(v);
          if (!v) setDupMatches(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('customer.newCustomerTitle')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>{t('overview.company')}</Label>
              <Input
                value={newName}
                onChange={e => {
                  setNewName(e.target.value);
                  setDupMatches(null);
                }}
                onKeyDown={e => e.key === 'Enter' && handleNewCustomer()}
              />
            </div>
            <div>
              <Label>{t('customer.worker')}</Label>
              <Select value={newWorker} onValueChange={setNewWorker}>
                <SelectTrigger>
                  <SelectValue placeholder={t('overview.allWorkers')} />
                </SelectTrigger>
                <SelectContent>
                  {workers.map(w => (
                    <SelectItem key={w.id} value={w.name}>
                      {w.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {dupMatches && (
              <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm">
                <div className="flex items-center gap-2 font-medium text-destructive">
                  <AlertTriangle className="h-4 w-4" />
                  {t('customer.dupWarning')}
                </div>
                <ul className="mt-2 space-y-1 text-muted-foreground">
                  {dupMatches.map(c => (
                    <li key={c.id}>
                      {c.name}
                      {c.city ? ` — ${c.city}` : ''}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant={dupMatches ? 'destructive' : 'default'}
              onClick={handleNewCustomer}
            >
              {dupMatches ? t('customer.createAnyway') : t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
